import { createStore } from '@xstate/store'

type ComponentName =
  | 'RangeTable'
  | 'Root'
  | 'NodeLockButton'
  | 'MobileMenu'
  | '__some_other_component__'
export type WiredComponent = ComponentName
export type RootId = string

type ComponentMountInfo = {
  componentRootId: RootId
  componentName: ComponentName
  props?: Record<string, any>
}
type Context = {
  mountedComponents: Map<ComponentName, Map<RootId, ComponentMountInfo>>
}

const initialContext: Context = {
  mountedComponents: new Map<ComponentName, Map<RootId, ComponentMountInfo>>(),
}

export const componentsBridge = createStore({
  context: initialContext,
  emits: {
    componentShouldMount: (
      _payload: {
        componentRootId: RootId
        componentName: ComponentName
      } & ComponentMountInfo,
    ) => {},
    componentShouldUnmount: (_payload: {
      componentRootId: RootId
      componentName: ComponentName
    }) => {},
  },
  on: {
    mountComponent: (context, event: ComponentMountInfo, enqueue) => {
      enqueue.emit.componentShouldMount(event)

      const existingComponents = context.mountedComponents.get(event.componentName) || new Map()
      const updatedComponents = existingComponents.set(event.componentRootId, {
        componentName: event.componentName,
        componentRootId: event.componentRootId,
        props: event.props,
      })

      context.mountedComponents.set(event.componentName, updatedComponents)

      return {
        mountedComponents: new Map(context.mountedComponents),
      }
    },
    unmountComponent: (
      context,
      event: { componentRootId: RootId; componentName: ComponentName },
      enqueue,
    ) => {
      enqueue.emit.componentShouldUnmount(event)
      const componentMap = context.mountedComponents.get(event.componentName)
      if (componentMap) {
        componentMap.delete(event.componentRootId)
      }

      return {
        mountedComponents: new Map(context.mountedComponents),
      }
    },
    updateComponentProps: (
      context,
      event: { componentRootId: RootId; componentName: ComponentName } & ComponentMountInfo,
      enqueue,
    ) => {
      const existingComponents = context.mountedComponents.get(event.componentName) || new Map()
      existingComponents.set(event.componentRootId, {
        componentName: event.componentName,
        componentRootId: event.componentRootId,
        props: event.props,
      })
      context.mountedComponents.set(event.componentName, existingComponents)

      return {
        mountedComponents: new Map(context.mountedComponents),
      }
    },
  },
})

export const getComponentsByName = (componentName: ComponentName) => {
  return componentsBridge.getSnapshot().context.mountedComponents.get(componentName)
}
